import React from 'react';
import {getDataFromTree} from '@apollo/client/react/ssr';
import {ApolloClient, CombinedGraphQLErrors} from '@apollo/client';
import {withRouter} from 'next/router';

import {initApollo} from '../apollo/init-apollo';
import type {SsrStatus} from './seo/ssr-status';
import {SsrDataPassProvider} from './ssr-data-pass';

/**
 * Выполняет запросы страницы на сервере и передаёт кэш Apollo в браузер.
 *
 * За счёт этого запрос описан ровно один раз — в самом компоненте (`useXQuery({ssr: true})`):
 * getDataFromTree находит его при серверном рендере, а клиент поднимает результат из
 * apolloState и повторно в сеть не ходит.
 */
export const withApolloClient = (App: any) => {
	class Apollo extends React.Component {
		static displayName = `withApolloClient(${App.displayName || App.name})`;

		static async getInitialProps(appContext: any) {
			const {Component, router, ctx} = appContext;
			const isServer = typeof window === 'undefined';

			const apolloClient = isServer ? initApollo(null, ctx) : undefined;

			let appProps = {};
			if (App.getInitialProps) {
				appProps = await App.getInitialProps({...appContext, apolloClient});
			}

			const apolloState: any = {};
			// сюда карточки при серверном рендере отмечают, что сущность не найдена
			const ssrStatus: SsrStatus = {};

			if (isServer) {
				try {
					// один проход по дереву: getDataFromTree сам рендерит его столько раз,
					// сколько нужно, чтобы выполнить все вложенные запросы
					await getDataFromTree(
						<SsrDataPassProvider value={true}>
							<App
								{...appProps}
								Component={Component}
								router={router}
								apolloState={apolloState}
								apolloClient={apolloClient}
								ssrStatus={ssrStatus}
							/>
						</SsrDataPassProvider>,
					);
				} catch (error) {
					// ошибки GraphQL не должны ронять SSR — компоненты покажут их через data.error
					if (CombinedGraphQLErrors.is(error as Error)) {
						const code = (error as CombinedGraphQLErrors).errors[0]?.extensions?.code;
						if (code && ctx.res) ctx.res.statusCode = Number(code) || 500;
					}
					console.log(error);
				}

				if (ssrStatus.notFound && ctx.res) ctx.res.statusCode = 404;

				apolloState.data = apolloClient && apolloClient.cache.extract();
			}

			return {
				...appProps,
				apolloState,
				// ошибки Apollo в кэш не попадают, и финальный рендер уже не знает о «не найдено» — передаём флаг явно
				ssrNotFound: !!ssrStatus.notFound,
			};
		}

		apolloClient: ApolloClient;

		constructor(props: any) {
			super(props);
			// на сервере клиент уже создан в getInitialProps, в браузере поднимаем кэш из apolloState
			this.apolloClient = props.apolloClient || initApollo(props.apolloState.data);
		}

		render() {
			if ('ShowData' in (this.props as any).router.query) {
				return <pre>{JSON.stringify(this.apolloClient.cache.extract(), null, 2)}</pre>;
			}
			return <App {...this.props} apolloClient={this.apolloClient} />;
		}
	}
	return withRouter(Apollo as any);
};
